/**
 * imageCompressor.js — Client-side image compression and scaling for Vision AI.
 *
 * Scales images to optimal Vision AI resolutions (max 1280px dimension)
 * and compresses to high-efficiency WebP/JPEG to minimize network payload
 * while preserving high fidelity for OCR, charts, and item tooltips.
 */

export async function compressImage(fileOrDataUrl, {
  maxDimension = 1280,
  quality = 0.82,
  onProgress = () => {},
} = {}) {
  onProgress({ status: 'reading', progress: 0.1, message: 'Reading image data…' });

  let imgSource = fileOrDataUrl;
  let originalSize = 0;

  if (fileOrDataUrl instanceof Blob || fileOrDataUrl instanceof File) {
    originalSize = fileOrDataUrl.size;
    imgSource = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(fileOrDataUrl);
    });
  } else if (typeof fileOrDataUrl === 'string') {
    originalSize = Math.round((fileOrDataUrl.length * 3) / 4);
  }

  onProgress({ status: 'scaling', progress: 0.4, message: 'Optimizing resolution for Vision AI…' });

  const img = await new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error('Failed to decode image.'));
    el.src = imgSource;
  });

  let width = img.naturalWidth || img.width;
  let height = img.naturalHeight || img.height;

  if (width > maxDimension || height > maxDimension) {
    if (width > height) {
      height = Math.round((height * maxDimension) / width);
      width = maxDimension;
    } else {
      width = Math.round((width * maxDimension) / height);
      height = maxDimension;
    }
  }

  onProgress({ status: 'compressing', progress: 0.7, message: 'Compressing image…' });

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);

  // Export as WebP if supported, fallback to JPEG
  let mimeType = 'image/jpeg';
  let compressedDataUrl = canvas.toDataURL(mimeType, quality);

  const compressedSize = Math.round((compressedDataUrl.length * 3) / 4);
  const ratio = originalSize > 0 ? Math.round((1 - compressedSize / originalSize) * 100) : 0;

  onProgress({
    status: 'done',
    progress: 1.0,
    message: `Ready (${width}x${height}, ${(compressedSize / 1024).toFixed(1)} KB${ratio > 0 ? `, -${ratio}%` : ''})`,
  });

  return {
    dataUrl: compressedDataUrl,
    width,
    height,
    originalSize,
    compressedSize,
    mimeType,
  };
}
